import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from "@/components/ui/breadcrumb";
import { UserMenu } from '@/components/auth/UserMenu';
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useAuth } from '@/components/auth/AuthProvider';
import { useAdminAuth } from '@/hooks/useAdminAuth';

const routeLabels: Record<string, string> = {
  '/': 'Dashboard',
  '/orders': 'Active Orders',
  '/archived-orders': 'Archived Orders',
  '/non-scanning-products': 'Missing Products',
  '/executive-reporting': 'Executive Reporting',
  '/ai-features': 'AI Features',
  '/ai-advanced': 'Advanced AI',
  '/analytics': 'Analytics Dashboard',
  '/sync-manager': 'Unified Sync Center',
  '/sync-control': 'Sync Control',
  '/data-recovery': 'Data Recovery',
  '/master-products': 'Master Products',
  '/product-families': 'Product Families',
  '/suppliers': 'Suppliers',
  '/edits-awaiting-approval': 'Edits Awaiting Approval',
  '/credit-request': 'Credit Requests',
  '/employee-management': 'Employee Management',
  '/payroll-processing': 'Payroll Processing',
};

const generateBreadcrumbs = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs = [{ label: 'Dashboard', href: '/' }];
  
  let currentPath = '';
  segments.forEach((segment) => {
    currentPath += `/${segment}`;
    const label = routeLabels[currentPath] || segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    breadcrumbs.push({ label, href: currentPath });
  });
  
  return breadcrumbs;
};

export const AppHeader = () => {
  const location = useLocation();
  const { profile } = useAuth();
  const { isAdminAuthenticated } = useAdminAuth();
  const breadcrumbs = generateBreadcrumbs(location.pathname);

  // Determine effective role: SMS staff auth overrides profile role display
  const getEffectiveRole = (): 'admin' | 'manager' | 'staff' | null => {
    try {
      const raw = localStorage.getItem('staff_sms_auth');
      if (raw) {
        const data = JSON.parse(raw);
        const now = Date.now();
        if (data?.authenticated && typeof data.expiry === 'number' && data.expiry > now) {
          return 'staff';
        }
      }
    } catch (e) {
      console.warn('Failed to parse staff_sms_auth');
    }
    return (profile?.role as any) || null;
  };
  const effectiveRole = getEffectiveRole();


  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex items-center gap-2 px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={`breadcrumb-${index}-${crumb.href}`}>
                <BreadcrumbItem>
                  {index === breadcrumbs.length - 1 ? (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={crumb.href}>{crumb.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      
      <div className="ml-auto flex items-center gap-2 px-4">
        {effectiveRole && (
          <Badge 
            variant="secondary"
          >
            {effectiveRole} Role
          </Badge>
        )}
        
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
};